import { useState } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { getUserErrors } from "common-client/utils/apollo";
import {
  GetCustomMapsForSettingsDocument,
  useDeleteCustomMapMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { DeleteModalFieldset } from "../../../AddressPanel/Header/__styles__/DeletePropertyModal";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
} from "../../../Common/__styles__/Modal";
import { Button } from "../../../Common/Buttons/Button";
import Modal from "../../../Common/Modal";
import { Body, Title } from "../../../Common/Typography";
import { Checkbox } from "../../../Inputs/react-hook-form";
import { ReasonsList, ReasonsListItem } from "./__styles__/MapObjects";
import { CustomMapForSettings } from "./types";

interface DeleteConfirmationForm {
  confirmObjectsDelete: boolean;
  confirmAssociatedDataDelete: boolean;
  confirmDashboardRemoval: boolean;
}

export const DeleteCustomMapModalContent = ({
  customMap,
  closeModal,
  onDelete,
}: {
  customMap: CustomMapForSettings;
  closeModal: () => void;
  onDelete?: () => void;
}) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { deletable, reasons } = customMap.canDelete;

  const {
    formState: { isValid },
    control,
  } = useForm<DeleteConfirmationForm>({
    defaultValues: {
      confirmObjectsDelete: false,
      confirmAssociatedDataDelete: false,
      confirmDashboardRemoval: false,
    },
  });

  const [deleteCustomMap, { loading }] = useDeleteCustomMapMutation({
    refetchQueries: [GetCustomMapsForSettingsDocument],
    onCompleted: () => {
      addSuccessToast(`"${customMap.name}" deleted`);
      onDelete?.();
      closeModal();
    },
    onError: error => {
      const userError = getUserErrors(error);

      if (userError) {
        addErrorToast(userError);
      } else {
        addErrorToast(
          `An error occurred while deleting the ${noun}. Please try again.`,
        );
      }
    },
  });

  const handleDelete = async () => {
    await deleteCustomMap({ variables: { id: customMap.id } });
  };

  const noun = customMap.isObject ? "map object type" : "custom map";

  const objectCountLabel =
    customMap.isObject ?
      `${customMap.geometryCount} ${customMap.name}`
    : customMap.name;

  return (
    <Container>
      <HeaderSection>
        <Title size="small" type="semiBold">
          Delete {noun}
        </Title>
        <Body type="regular" size="large" color="contentSecondary">
          {objectCountLabel}
        </Body>
      </HeaderSection>
      <FormSection>
        <ContentSection>
          {!deletable && (
            <>
              <Body size="default" type="regular">
                This {noun} can't be deleted:
              </Body>
              <ReasonsList>
                {reasons.map((reason, index) => (
                  <ReasonsListItem key={index}>
                    <Body size="default" type="regular">
                      {reason}
                    </Body>
                  </ReasonsListItem>
                ))}
              </ReasonsList>
            </>
          )}
          {deletable && customMap.isObject && (
            <>
              <Body size="default" type="regular">
                By deleting this {noun}, you confirm that:
              </Body>
              <DeleteModalFieldset>
                <Checkbox
                  control={control}
                  required={true}
                  name="confirmObjectsDelete"
                  label={`All ${customMap.name} objects will be deleted.`}
                />
                <Checkbox
                  control={control}
                  required={true}
                  name="confirmAssociatedDataDelete"
                  label="All associated files, records and activity will also be deleted."
                />
                <Checkbox
                  control={control}
                  required={true}
                  name="confirmDashboardRemoval"
                  label="The records will be removed from the dashboard for all users."
                />
              </DeleteModalFieldset>
              <Body size="default" type="regular">
                This action <strong>cannot</strong> be undone.
              </Body>
            </>
          )}
          {deletable && !customMap.isObject && (
            <Body size="default" type="regular">
              This action <strong>cannot</strong> be undone.
            </Body>
          )}
        </ContentSection>
        {deletable && (
          <ButtonSection>
            <PrimaryButtons>
              <Button
                variant="secondary"
                size="medium"
                onPress={closeModal}
                title="Cancel"
              />
              <Button
                variant="critical"
                isDisabled={!isValid && customMap.isObject}
                isPending={loading}
                onPress={handleDelete}
                size="medium"
                title="Delete"
              />
            </PrimaryButtons>
          </ButtonSection>
        )}
      </FormSection>
    </Container>
  );
};

export function useDeleteCustomMapModal({
  onDelete,
}: {
  onDelete?: () => void;
}) {
  const [customMap, setCustomMap] = useState<Maybe<CustomMapForSettings>>(null);

  const [showModal, hideModal] = useModal(
    () =>
      customMap && (
        <Modal onRequestClose={hideModal}>
          <DeleteCustomMapModalContent
            customMap={customMap}
            closeModal={hideModal}
            onDelete={onDelete}
          />
        </Modal>
      ),
    [customMap, onDelete],
  );

  const showDeleteModal = (map: CustomMapForSettings) => {
    setCustomMap(map);
    showModal();
  };

  return showDeleteModal;
}
